import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Desktop scrolls the table; mobile uses the existing workspace page scroll.
// All lines remain in form state, calculation requests and autosave payloads.
export function usePurchaseRows(lines) {
  const [element, registerContainer] = useState(null);
  const sizes = useRef(new Map()), nodes = useRef(new Map()), observer = useRef(null);
  const [revision, setRevision] = useState(0);
  const [viewport, setViewport] = useState({ top: 0, height: 500, desktop: window.innerWidth >= 1024 });
  const refresh = useCallback(() => {
    if (!element) return;
    const desktop = window.innerWidth >= 1024;
    const scroller = desktop ? element : element.parentElement;
    const tableOffset = desktop ? 0 : element.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    const next = { top: Math.max(0, scroller.scrollTop - tableOffset), height: scroller.clientHeight, desktop };
    setViewport(old => old.top === next.top && old.height === next.height && old.desktop === next.desktop ? old : next);
  }, [element]);
  useLayoutEffect(() => {
    if (!element) return;
    const resize = new ResizeObserver(refresh); resize.observe(element); resize.observe(element.parentElement);
    element.addEventListener('scroll', refresh, { passive: true }); element.parentElement.addEventListener('scroll', refresh, { passive: true }); window.addEventListener('resize', refresh);
    observer.current = new ResizeObserver(entries => {
      let changed = false;
      for (const entry of entries) {
        const id = entry.target.dataset.lineId, height = entry.target.getBoundingClientRect().height;
        if (height > 0 && Math.abs((sizes.current.get(id) || 140) - height) > 1) { sizes.current.set(id, height); changed = true; }
      }
      if (changed) setRevision(value => value + 1);
    });
    nodes.current.forEach(node => observer.current.observe(node)); refresh();
    return () => { resize.disconnect(); observer.current.disconnect(); element.removeEventListener('scroll', refresh); element.parentElement.removeEventListener('scroll', refresh); window.removeEventListener('resize', refresh); };
  }, [element, refresh]);
  const registerRow = useCallback((id, node) => {
    const old = nodes.current.get(id); if (old) observer.current?.unobserve(old);
    if (node) { nodes.current.set(id, node); observer.current?.observe(node); }
    else nodes.current.delete(id);
  }, []);
  const offsets = useMemo(() => {
    const values = [0];
    for (const line of lines) values.push(values[values.length - 1] + (sizes.current.get(line.lineId) || 140));
    return values;
    // Measurements are deliberately kept out of each line's input state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, revision]);
  const enabled = lines.length > 80;
  let start = 0, end = lines.length;
  if (enabled) {
    while (start < lines.length && offsets[start + 1] < viewport.top) start++;
    end = start;
    while (end < lines.length && offsets[end] < viewport.top + viewport.height) end++;
    start = Math.max(0, start - 4); end = Math.min(lines.length, end + 5);
  }
  const ensureVisible = id => {
    const index = lines.findIndex(line => line.lineId === id);
    if (index < 0 || !element) return;
    const scroller = window.innerWidth >= 1024 ? element : element.parentElement;
    const tableOffset = scroller === element ? 0 : element.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    scroller.scrollTop = tableOffset + offsets[index]; refresh();
  };
  return { start, end, top: enabled ? offsets[start] : 0,
    bottom: enabled ? offsets[lines.length] - offsets[end] : 0, registerContainer, registerRow, ensureVisible };
}
